import { z } from "zod";
import { schemas } from "../generated/api/client";

/** API form Schema */
export const zFormSchema = schemas.PartialEBulkApplicationData
export type FormSchema = z.infer<typeof zFormSchema>

/**
 * Local form Schema
 * 
 * We extend the API form schema for local use, serving two purposes:
 * 1. To add local fields for transient state which doesn't get sent to the server (e.g. _hasPassport, _hasDriverLicence)
 * 2. To add null wrappers to optional fields, so they can be folded down to undefined on save (React Form Hook doesn't support using undefined as a value for reset/setValue)
 * 
 * @see foldLocalFormSchemaToApiFormSchema for the folding down operation
 */
export const zLocalFormSchema = zFormSchema.extend({
  // Optional Fields (add null wrappers which will fold down to undefined on save)
  // Empty strings on some fields are used to clear them
  Middlenames: z.object({
    Middlename: zFormSchema.shape.Middlenames.unwrap().shape.Middlename,
  }).optional().nullable(),
  CurrentAddress: zFormSchema.shape.CurrentAddress.unwrap().extend({
    Address: zFormSchema.shape.CurrentAddress.unwrap().shape.Address.extend({
      AddressLine2: zFormSchema.shape.CurrentAddress.unwrap().shape.Address.shape.AddressLine2.or(z.literal('')).nullable(),
      AddressCounty: zFormSchema.shape.CurrentAddress.unwrap().shape.Address.shape.AddressCounty.or(z.literal('')).nullable(),
      // Postcode is optional only if the country code is not GB
      Postcode: zFormSchema.shape.CurrentAddress.unwrap().shape.Address.shape.Postcode.or(z.literal('')).nullable(),
    }),
  }).optional(),
  PreviousAddress: z.array(zFormSchema.shape.PreviousAddress.unwrap().element.extend({
    Address: zFormSchema.shape.CurrentAddress.unwrap().shape.Address.extend({
      AddressLine2: zFormSchema.shape.CurrentAddress.unwrap().shape.Address.shape.AddressLine2.or(z.literal('')).nullable(),
      AddressCounty: zFormSchema.shape.CurrentAddress.unwrap().shape.Address.shape.AddressCounty.or(z.literal('')).nullable(),
      // Postcode is optional only if the country code is not GB
      Postcode: zFormSchema.shape.CurrentAddress.unwrap().shape.Address.shape.Postcode.or(z.literal('')).nullable(),
    }),
  })).optional(),
  AdditionalApplicantDetails: zFormSchema.shape.AdditionalApplicantDetails.unwrap().extend({
    ContactNumber: zFormSchema.shape.AdditionalApplicantDetails.unwrap().shape.ContactNumber.or(z.literal('')).nullable(),
    BirthSurname: zFormSchema.shape.AdditionalApplicantDetails.unwrap().shape.BirthSurname.or(z.literal('')).nullable(),
    BirthSurnameUntil: zFormSchema.shape.AdditionalApplicantDetails.unwrap().shape.BirthSurnameUntil.or(z.literal('')).nullable(),
    BirthCounty: zFormSchema.shape.AdditionalApplicantDetails.unwrap().shape.BirthCounty.or(z.literal('')).nullable(),
    BirthNationality: zFormSchema.shape.AdditionalApplicantDetails.unwrap().shape.BirthNationality.or(z.literal('')).nullable(),
    OtherForenames: z.object({
      OtherForename: zFormSchema.shape.AdditionalApplicantDetails.unwrap().shape.OtherForenames.unwrap().shape.OtherForename.min(0),
    }).optional(),
    OtherSurnames: z.object({
      OtherSurname: zFormSchema.shape.AdditionalApplicantDetails.unwrap().shape.OtherSurnames.unwrap().shape.OtherSurname.min(0),
    }).optional(),
  }).optional(), // TODO: find out additionalApplicantDetails needs to be nullable / optional, because it might be lots with the unwrapping
  ApplicantIdentityDetails: zFormSchema.shape.ApplicantIdentityDetails.unwrap().extend({
    PassportDetails: zFormSchema.shape.ApplicantIdentityDetails.unwrap().shape.PassportDetails.unwrap().extend({
      PassportNumber: zFormSchema.shape.ApplicantIdentityDetails.unwrap().shape.PassportDetails.unwrap().shape.PassportNumber.nullable(),
      PassportIssueDate: zFormSchema.shape.ApplicantIdentityDetails.unwrap().shape.PassportDetails.unwrap().shape.PassportIssueDate.nullable(),
      PassportDob: zFormSchema.shape.ApplicantIdentityDetails.unwrap().shape.PassportDetails.unwrap().shape.PassportDob.nullable(),
      PassportNationality: zFormSchema.shape.ApplicantIdentityDetails.unwrap().shape.PassportDetails.unwrap().shape.PassportNationality.nullable(),
    }).optional().nullable(),
    DriverLicenceDetails: zFormSchema.shape.ApplicantIdentityDetails.unwrap().shape.DriverLicenceDetails.unwrap().extend({
      DriverLicenceNumber: zFormSchema.shape.ApplicantIdentityDetails.unwrap().shape.DriverLicenceDetails.unwrap().shape.DriverLicenceNumber.nullable(),
      DriverLicenceDOB: zFormSchema.shape.ApplicantIdentityDetails.unwrap().shape.DriverLicenceDetails.unwrap().shape.DriverLicenceDOB.nullable(),
      DriverLicenceType: zFormSchema.shape.ApplicantIdentityDetails.unwrap().shape.DriverLicenceDetails.unwrap().shape.DriverLicenceType.nullable(),
      DriverLicenceValidFrom: zFormSchema.shape.ApplicantIdentityDetails.unwrap().shape.DriverLicenceDetails.unwrap().shape.DriverLicenceValidFrom.nullable(),
      DriverLicenceIssueCountry: zFormSchema.shape.ApplicantIdentityDetails.unwrap().shape.DriverLicenceDetails.unwrap().shape.DriverLicenceIssueCountry.nullable(),
    }).optional().nullable(),
  }).optional(),
  // Local Fields
  // (all must be local, as they're not in the API form schema, so we can delete them in the folding down operation)
  _hasPassport: z.boolean().optional(),
  _hasDriverLicence: z.boolean().optional(),
  _clearFields: z.object({
    'Middlenames': z.string().optional(),
    'ApplicantIdentityDetails/PassportDetails': z.string().optional(),
    'ApplicantIdentityDetails/DriverLicenceDetails': z.string().optional(),
    'AdditionalApplicantDetails/ContactNumber': z.string().optional(),
    'AdditionalApplicantDetails/BirthSurname': z.string().optional(),
    'AdditionalApplicantDetails/BirthSurnameUntil': z.string().optional(),
    'AdditionalApplicantDetails/BirthCounty': z.string().optional(),
    'AdditionalApplicantDetails/BirthNationality': z.string().optional(),
  }).optional(),
})
export type LocalFormSchema = z.infer<typeof zLocalFormSchema>
export type LocalFormSchemaSuperRefineFn = Parameters<typeof zLocalFormSchema.superRefine>[0]

/**
 * DBS-355: the middle names actually entered. The page starts with one empty box, and a box left blank means no middle
 * name, so blank ones are dropped rather than sent (the api refuses an empty Middlename). Undefined when none are left.
 */
export const enteredMiddlenames = (middlenames: LocalFormSchema['Middlenames']): { Middlename: string[] } | undefined => {
  const entered = (middlenames?.Middlename ?? []).filter(each => typeof each === 'string' && each.trim() !== '')
  return entered.length > 0 ? { Middlename: entered } : undefined
}

/**
 * Folds down the local form schema to the API form schema by:
 * > Coercising all null wrappers to undefined
 * > Deleting all local fields
 */
export const convertLocalFormSchemaToApiFormSchema = (localFormData: LocalFormSchema): FormSchema => {
  // Upper Case the fields that are present
  const convertStringToUpperCase = (value: string): string => {
    return value.toUpperCase();
  }
  const convertStringOrUndefinedToUpperCase = (value: string | undefined): string | undefined => {
    if (value == null) return value;
    return value.toUpperCase();
  }
  const convertStringOrNullToUpperCase = (value: string | null): string | null => {
    if (value == null) return value;
    return value.toUpperCase();
  }
  const convertStringOrUndefinedOrNullToUpperCase = (value: string | null | undefined): string | null | undefined => {
    if (value == null) return value;
    return value.toUpperCase();
  }

  const convertAddressToUpperCase = (address: NonNullable<LocalFormSchema['CurrentAddress']>['Address'] | NonNullable<LocalFormSchema['PreviousAddress']>[number]['Address']) => {
    return {
      ...address,
      AddressLine1: convertStringToUpperCase(address.AddressLine1),
      AddressLine2: convertStringOrUndefinedOrNullToUpperCase(address.AddressLine2),
      AddressTown: convertStringToUpperCase(address.AddressTown),
      AddressCounty: convertStringOrUndefinedOrNullToUpperCase(address.AddressCounty),
      Postcode: convertStringOrUndefinedOrNullToUpperCase(address.Postcode),
    }
  }

  localFormData.Forename = convertStringOrUndefinedToUpperCase(localFormData.Forename)
  localFormData.Middlenames = localFormData.Middlenames == null
    ? localFormData.Middlenames
    : {
      Middlename: localFormData.Middlenames.Middlename.map(convertStringToUpperCase),
    }
  localFormData.PresentSurname = convertStringOrUndefinedToUpperCase(localFormData.PresentSurname)
  localFormData.NiNumber = convertStringOrUndefinedToUpperCase(localFormData.NiNumber)
  localFormData.CurrentAddress = localFormData.CurrentAddress == null
    ? localFormData.CurrentAddress
    : localFormData.CurrentAddress.Address == null
      ? localFormData.CurrentAddress
      : {
        ...localFormData.CurrentAddress,
        Address: convertAddressToUpperCase(localFormData.CurrentAddress.Address),
      }
  localFormData.PreviousAddress = localFormData.PreviousAddress == null
    ? localFormData.PreviousAddress
    : localFormData.PreviousAddress.map(previousAddress => ({
      ...previousAddress,
      Address: previousAddress.Address == null
        ? previousAddress.Address
        : convertAddressToUpperCase(previousAddress.Address),
    }))
  localFormData.AdditionalApplicantDetails = localFormData.AdditionalApplicantDetails == null
    ? localFormData.AdditionalApplicantDetails
    : {
      ...localFormData.AdditionalApplicantDetails,
      ContactNumber: convertStringOrUndefinedOrNullToUpperCase(localFormData.AdditionalApplicantDetails.ContactNumber),
      BirthSurname: convertStringOrUndefinedOrNullToUpperCase(localFormData.AdditionalApplicantDetails.BirthSurname),
      BirthTown: convertStringOrUndefinedToUpperCase(localFormData.AdditionalApplicantDetails.BirthTown),
      BirthCounty: convertStringOrUndefinedOrNullToUpperCase(localFormData.AdditionalApplicantDetails.BirthCounty),
      BirthNationality: convertStringOrUndefinedOrNullToUpperCase(localFormData.AdditionalApplicantDetails.BirthNationality),
      OtherForenames: localFormData.AdditionalApplicantDetails.OtherForenames == null
        ? localFormData.AdditionalApplicantDetails.OtherForenames
        : {
          OtherForename: localFormData.AdditionalApplicantDetails.OtherForenames.OtherForename.map(each => ({
            ...each,
            Name: convertStringToUpperCase(each.Name),
          })),
        },
      OtherSurnames: localFormData.AdditionalApplicantDetails.OtherSurnames == null
        ? localFormData.AdditionalApplicantDetails.OtherSurnames
        : {
          OtherSurname: localFormData.AdditionalApplicantDetails.OtherSurnames.OtherSurname.map(each => ({
            ...each,
            Name: convertStringToUpperCase(each.Name),
          })),
        },
    }
  localFormData.ApplicantIdentityDetails = localFormData.ApplicantIdentityDetails == null
    ? localFormData.ApplicantIdentityDetails
    : {
      ...localFormData.ApplicantIdentityDetails,
      PassportDetails: localFormData.ApplicantIdentityDetails.PassportDetails == null
        ? localFormData.ApplicantIdentityDetails.PassportDetails
        : {
          ...localFormData.ApplicantIdentityDetails.PassportDetails,
          PassportNationality: convertStringOrNullToUpperCase(localFormData.ApplicantIdentityDetails.PassportDetails.PassportNationality),
        },
      DriverLicenceDetails: localFormData.ApplicantIdentityDetails.DriverLicenceDetails == null
        ? localFormData.ApplicantIdentityDetails.DriverLicenceDetails
        : {
          ...localFormData.ApplicantIdentityDetails.DriverLicenceDetails,
          DriverLicenceNumber: convertStringOrNullToUpperCase(localFormData.ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceNumber),
        },
    }

  // Convert empty strings to undefined, so they can be folded down to undefined on save
  const convertEmptyStringToUndefined = (value: string | null | undefined) => value === '' ? undefined : value

  const apiFormData = {
    ...localFormData,
    Middlenames: enteredMiddlenames(localFormData.Middlenames),
    AdditionalApplicantDetails: {
      ...localFormData.AdditionalApplicantDetails,
      ContactNumber: convertEmptyStringToUndefined(localFormData.AdditionalApplicantDetails?.ContactNumber) ?? undefined,
      BirthSurname: convertEmptyStringToUndefined(localFormData.AdditionalApplicantDetails?.BirthSurname) ?? undefined,
      BirthSurnameUntil: convertEmptyStringToUndefined(localFormData.AdditionalApplicantDetails?.BirthSurnameUntil) ?? undefined,
      BirthCounty: convertEmptyStringToUndefined(localFormData.AdditionalApplicantDetails?.BirthCounty) ?? undefined,
      BirthNationality: convertEmptyStringToUndefined(localFormData.AdditionalApplicantDetails?.BirthNationality) ?? undefined,
      OtherForenames: localFormData.AdditionalApplicantDetails?.OtherForenames ?? undefined,
      OtherSurnames: localFormData.AdditionalApplicantDetails?.OtherSurnames ?? undefined,
    },
    CurrentAddress: localFormData.CurrentAddress
      ? {
        ...localFormData.CurrentAddress,
        Address: {
          ...localFormData.CurrentAddress.Address,
          AddressLine2: convertEmptyStringToUndefined(localFormData.CurrentAddress.Address.AddressLine2) ?? undefined,
          AddressCounty: convertEmptyStringToUndefined(localFormData.CurrentAddress.Address.AddressCounty) ?? undefined,
          Postcode: convertEmptyStringToUndefined(localFormData.CurrentAddress.Address.Postcode) ?? undefined,
        },
      }
      : undefined,
    PreviousAddress: localFormData.PreviousAddress
      ? [
        ...localFormData.PreviousAddress.map(address => ({
          ...address,
          Address: {
            ...address.Address,
            AddressLine2: convertEmptyStringToUndefined(address.Address.AddressLine2) ?? undefined,
            AddressCounty: convertEmptyStringToUndefined(address.Address.AddressCounty) ?? undefined,
            Postcode: convertEmptyStringToUndefined(address.Address.Postcode) ?? undefined,
          },
        })),
      ]
      : undefined,
    ApplicantIdentityDetails: (
      localFormData.ApplicantIdentityDetails?.PassportDetails?.PassportNumber == null
      && localFormData.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceNumber == null
    ) ? undefined
      : {
        ...localFormData.ApplicantIdentityDetails ?? {},
        PassportDetails: (
          localFormData.ApplicantIdentityDetails?.PassportDetails?.PassportNumber == null
          || localFormData.ApplicantIdentityDetails?.PassportDetails?.PassportIssueDate == null
          || localFormData.ApplicantIdentityDetails?.PassportDetails?.PassportDob == null
          || localFormData.ApplicantIdentityDetails?.PassportDetails?.PassportNationality == null
        ) ? undefined
          : {
            ...localFormData.ApplicantIdentityDetails?.PassportDetails ?? {},
            PassportNumber: localFormData.ApplicantIdentityDetails?.PassportDetails?.PassportNumber,
            PassportIssueDate: localFormData.ApplicantIdentityDetails?.PassportDetails?.PassportIssueDate,
            PassportDob: localFormData.ApplicantIdentityDetails?.PassportDetails?.PassportDob,
            PassportNationality: localFormData.ApplicantIdentityDetails?.PassportDetails?.PassportNationality,
          },
        DriverLicenceDetails:
          localFormData.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceNumber == null
            || localFormData.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceDOB == null
            || localFormData.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceType == null
            || localFormData.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceValidFrom == null
            || localFormData.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceIssueCountry == null
            ? undefined
            : {
              ...localFormData.ApplicantIdentityDetails?.DriverLicenceDetails ?? {},
              DriverLicenceNumber: localFormData.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceNumber,
              DriverLicenceDOB: localFormData.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceDOB,
              DriverLicenceType: localFormData.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceType,
              DriverLicenceValidFrom: localFormData.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceValidFrom,
              DriverLicenceIssueCountry: localFormData.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceIssueCountry,
            },
      },
  } satisfies FormSchema

  if (apiFormData.AdditionalApplicantDetails?.OtherForenames?.OtherForename?.length === 0) {
    delete apiFormData.AdditionalApplicantDetails.OtherForenames
  }
  if (apiFormData.AdditionalApplicantDetails?.OtherSurnames?.OtherSurname?.length === 0) {
    delete apiFormData.AdditionalApplicantDetails.OtherSurnames
  }

  delete apiFormData._hasPassport
  delete apiFormData._hasDriverLicence
  delete apiFormData._clearFields

  return apiFormData
}

/**
 * Generate the headers for the update request, which include:
 * * `X-DBS-API-Clear-Fields: string[] | undefined`: The fields to clear (as they're removed from the form schema, we need to tell the API to clear them)
 */
export const convertLocalFormSchemaToUpdateHeaders = (localFormData: LocalFormSchema) => {
  // Find the optional fields that are explicitly empty, so we can clear them
  const _clearFieldsIncludingOptional = {
    ...localFormData._clearFields,
    // DBS-355: every middle-name box left blank clears any middle name saved before
    'Middlenames': localFormData.Middlenames != null && enteredMiddlenames(localFormData.Middlenames) === undefined
      ? 'true'
      : localFormData._clearFields?.Middlenames,
    'AdditionalApplicantDetails/ContactNumber': localFormData.AdditionalApplicantDetails?.ContactNumber === ''
      ? 'true'
      : localFormData._clearFields?.["AdditionalApplicantDetails/ContactNumber"],
    'AdditionalApplicantDetails/BirthSurname': localFormData.AdditionalApplicantDetails?.BirthSurname === ''
      ? 'true'
      : localFormData._clearFields?.["AdditionalApplicantDetails/BirthSurname"],
    'AdditionalApplicantDetails/BirthSurnameUntil': localFormData.AdditionalApplicantDetails?.BirthSurnameUntil === ''
      ? 'true'
      : localFormData._clearFields?.["AdditionalApplicantDetails/BirthSurnameUntil"],
    'AdditionalApplicantDetails/BirthCounty': localFormData.AdditionalApplicantDetails?.BirthCounty === ''
      ? 'true'
      : localFormData._clearFields?.["AdditionalApplicantDetails/BirthCounty"],
    'AdditionalApplicantDetails/BirthNationality': localFormData.AdditionalApplicantDetails?.BirthNationality === ''
      ? 'true'
      : localFormData._clearFields?.["AdditionalApplicantDetails/BirthNationality"],
  }

  // Convert { Middlenames: true, something: false } to ['Middlenames']
  const _clearFieldsList = Object.entries(_clearFieldsIncludingOptional)
    .reduce((acc, [key, value]) => value === 'true' ? [...acc, key] : acc, [] as string[])

  const headers = {
    'x-dbs-api-clear-fields': _clearFieldsList,
  }

  return headers
}